import { IDataTableSavedFilter, IDataTableSavedState, isDataTableWrapMode } from '@srm/ui/data-table/state';
import { IDataTableColumnFilter, parseColumnWidth } from '@srm/ui/data-table';
import { IGridStateSettings, IGridStateSettingsColumn } from '@models/response/IGridStateSettings';
import { IDataGridColumnDescription } from '@models/response/IGridDescriptionResponse';
import { isServiceColumn } from './dataGridSchema';

/**
 * Сохранённые виды списка ↔ состояние базовой таблицы.
 *
 * Виды хранятся в том же формате, что и у DX-грида (`IGridStateSettings`):
 * бэкенд-хранилище общее, поэтому набор полей менять нельзя. Из него нам нужны
 * колонки (порядок, видимость, ширина, сортировка), фильтры и размер страницы —
 * остальное (`filterPanel`, `searchText`, `pageIndex`) сохраняем как есть, чтобы
 * вид, сделанный в старом гриде, не терял данные при пересохранении в новом.
 */

/** Вид → состояние таблицы. */
export function viewToTableState(view: IGridStateSettings | undefined): IDataTableSavedState | undefined {
    if (!view?.state) return undefined;
    const columns = view.state.columns ?? [];
    const columnVisibility: Record<string, boolean> = {};
    const columnSizing: Record<string, number> = {};
    const ordered: { field: string; index: number }[] = [];
    const sorted: { field: string; index: number; desc: boolean }[] = [];
    const grouped: { field: string; index: number }[] = [];
    const columnFilters: IDataTableSavedFilter[] = [];

    columns.forEach((column, position) => {
        const field = column.dataField ?? column.name;
        if (!field) return;
        if (column.visible === false) columnVisibility[field] = false;
        const width = parseColumnWidth(column.width);
        if (typeof width === 'number' && Number.isFinite(width) && width > 0) columnSizing[field] = width;
        ordered.push({ field, index: column.visibleIndex ?? position });
        if (column.sortOrder) {
            sorted.push({ field, index: column.sortIndex ?? 0, desc: column.sortOrder === 'desc' });
        }
        if (typeof column.groupIndex === 'number' && column.groupIndex >= 0) {
            grouped.push({ field, index: column.groupIndex });
        }
        const filter = viewColumnFilter(column);
        if (filter) columnFilters.push({ id: field, value: filter });
    });

    return {
        columnVisibility,
        columnSizing,
        columnOrder: ordered.sort((a, b) => a.index - b.index).map((c) => c.field),
        sorting: sorted.sort((a, b) => a.index - b.index).map((c) => ({ id: c.field, desc: c.desc })),
        grouping: grouped.sort((a, b) => a.index - b.index).map((c) => c.field),
        columnFilters,
        pageSize: view.state.pageSize || undefined,
        // tri-state канон; булев wrap — fallback для видов, сохранённых DX-гридом
        wrapMode: isDataTableWrapMode(view.state.wrapMode)
            ? view.state.wrapMode
            : view.state.wrap != null
              ? view.state.wrap
                  ? 'wrap'
                  : 'nowrap'
              : undefined,
    };
}

/**
 * Фильтр колонки из вида: строка фильтров (значение + операция) и отмеченные
 * значения воронки. Операции у обеих таблиц записаны одинаково (нотация DX),
 * поэтому переводить их не нужно.
 */
function viewColumnFilter(column: IGridStateSettingsColumn): IDataTableColumnFilter | undefined {
    const hasRow = column.filterValue !== undefined && column.filterValue !== null && column.filterValue !== '';
    const header = column.filterValues?.length ? column.filterValues : undefined;
    if (!hasRow && !header) return undefined;
    return {
        ...(hasRow
            ? {
                  row: {
                      op: column.selectedFilterOperation || 'contains',
                      value: column.filterValue as string | [string, string],
                  },
              }
            : {}),
        ...(header ? { header } : {}),
    };
}

/**
 * Состояние таблицы → вид. `previous` сохраняет поля, которыми новая таблица не
 * распоряжается (флаги выбранного/дефолтного вида, панель фильтров, поиск).
 */
export function tableStateToView(
    name: string,
    state: IDataTableSavedState,
    schemeColumns: IDataGridColumnDescription[],
    previous?: IGridStateSettings,
): IGridStateSettings {
    const order = state.columnOrder ?? [];
    const sortIndexOf = (field: string) => state.sorting?.findIndex((s) => s.id === field) ?? -1;
    const groupIndexOf = (field: string) => state.grouping?.indexOf(field) ?? -1;
    const filterOf = (field: string) =>
        state.columnFilters?.find((f) => f.id === field)?.value as IDataTableColumnFilter | undefined;

    const columns: IGridStateSettingsColumn[] = schemeColumns
        .filter((column) => !isServiceColumn(column))
        .map((column) => {
            const orderIndex = order.indexOf(column.field);
            const sortIndex = sortIndexOf(column.field);
            const groupIndex = groupIndexOf(column.field);
            const filter = filterOf(column.field);
            return {
                dataField: column.field,
                name: column.header,
                dataType: String(column.dataType ?? ''),
                visible: state.columnVisibility?.[column.field] !== false,
                visibleIndex: orderIndex >= 0 ? orderIndex : column.index,
                width: state.columnSizing?.[column.field],
                sortIndex: sortIndex >= 0 ? sortIndex : undefined,
                sortOrder: sortIndex >= 0 ? (state.sorting?.[sortIndex].desc ? 'desc' : 'asc') : undefined,
                groupIndex: groupIndex >= 0 ? groupIndex : undefined,
                filterValue: filter?.row?.value,
                selectedFilterOperation: filter?.row?.op,
                filterValues: filter?.header,
            };
        });

    return {
        ...(previous ?? { filters: null, sort: null, selected: false, default: false }),
        name,
        state: {
            ...(previous?.state ?? {
                allowedPageSizes: [],
                filterPanel: { filterEnabled: false },
                filterValue: '',
                searchText: '',
                pageIndex: 0,
                pageSize: 0,
            }),
            columns,
            pageSize: state.pageSize ?? previous?.state?.pageSize ?? 0,
            // «Обрезать» и «Без переноса» различимы только в wrapMode;
            // булев wrap пишем для видов, которые ещё откроет старый грид
            wrapMode: state.wrapMode,
            wrap: state.wrapMode === 'wrap',
        },
    };
}
